import {keepPreviousData, useInfiniteQuery} from '@tanstack/react-query';

import {apiClient} from '../../../../../utils/api-client';

export type CategoryId = string | 'root';

export interface CategoryTreeElement {
    categoryNodeId: CategoryId;
    // todo: can be null, write correct types

    children: CategoriesPageResponse;
    documentsCount: number;
    name: string;
}

export type CategoriesPageResponse = PageResponse<CategoryTreeElement, string>;

interface Props {
    /** Used to update the count of documents for the root category */
    categoryId: CategoryId;
    pageSize: 'variable' | number;
}

const CATEGORIES_PAGE_LIMITS = {
    firstPage: 11,
    secondPage: 89,
    anyPage: 100
};

export function getPageSize(offset = 0, pageSize: Props['pageSize'] = 'variable') {
    if (pageSize !== 'variable') {
        return pageSize;
    }

    if (offset >= CATEGORIES_PAGE_LIMITS.anyPage) {
        return CATEGORIES_PAGE_LIMITS.anyPage;
    }

    if (offset >= CATEGORIES_PAGE_LIMITS.firstPage) {
        return CATEGORIES_PAGE_LIMITS.secondPage;
    }

    return CATEGORIES_PAGE_LIMITS.firstPage;
}

export function useCategoriesInfiniteQuery(props: Props) {
    return useInfiniteQuery<CategoryTreeElement>({
        queryKey: ['categories', props.categoryId, props.pageSize],
        queryFn: (context) => {
            const pageParam = context.pageParam as PageParams;

            return apiClient.get<CategoryTreeElement>({
                path: 'editor-documents-service/api/documents/categories',
                query: {
                    cursor: pageParam.cursor,
                    limit: pageParam.limit,
                    'parent-category-node-id': props.categoryId === 'root' ? null : props.categoryId
                }
            });
        },
        getNextPageParam: ({children: {remaining, total, nextCursor}}) => {
            const hasNextPage = remaining > 0;

            if (hasNextPage) {
                return {
                    cursor: nextCursor,
                    limit: getPageSize(total - remaining, props.pageSize)
                };
            }
        },
        placeholderData: keepPreviousData,
        initialPageParam: {cursor: null, limit: getPageSize(0, props.pageSize)} as PageParams,
        notifyOnChangeProps: process.env.NODE_ENV === 'test' ? 'all' : undefined
    });
}
